import React from "react";
import classNames from "classnames";
import { useRouter } from "next/router";
import { useEffect } from "react";

import Typography, { ETypo, ETypoColor } from "../../Typography";
import classes from "./classes.module.scss";
import useHoverable from "@Front/Hooks/useHoverable";

export type IHeaderLinkProps = {
	text: string | React.ReactNode;
	path: string;
	routesActive?: string[];
	disabledLink?: boolean;
};

function ButtonHeaderTypography({
	isActive,
	isHovered,
	text,
	disabled,
}: { isActive: boolean; isHovered: boolean; text: string | React.ReactNode; disabled?: boolean }): React.ReactElement {
	const typo = isActive ? ETypo.TEXT_MD_SEMIBOLD : ETypo.TEXT_MD_REGULAR;
	const color = disabled ? ETypoColor.COLOR_NEUTRAL_500 : (isActive || isHovered ? ETypoColor.COLOR_NEUTRAL_950 : ETypoColor.COLOR_NEUTRAL_700);
	return (
		<Typography typo={typo} color={color} type="span">
			{text}
		</Typography>
	);
}

function buttonHeaderAccessibleLabel(text: string | React.ReactNode): string | undefined {
	if (typeof text === "string") {
		return text;
	}
	return undefined;
}

function useButtonHeaderActive(path: string, pathname: string, routesActive: string[] | undefined): boolean {
	const [isActive, setIsActive] = React.useState(path === pathname);
	useEffect(() => {
		let active = path === pathname;
		if (!active && routesActive) {
			active = routesActive.some((routeActive) => pathname.includes(routeActive));
		}
		setIsActive(active);
	}, [pathname, path, routesActive]);
	return isActive;
}

export default function ButtonHeader(props: IHeaderLinkProps): React.ReactElement {
	const { disabledLink, path, text, routesActive } = props;
	const router = useRouter();
	const isActive = useButtonHeaderActive(path, router.pathname, routesActive);
	const { handleMouseLeave, handleMouseEnter, isHovered } = useHoverable();

	const hasPath = path !== "" && path !== undefined;
	if (hasPath && !disabledLink) {
		return (
			<button
				type="button"
				className={classNames(classes["root"], isActive && classes["active"])}
				aria-label={buttonHeaderAccessibleLabel(text)}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onClick={() => void router.push(path)}>
				<ButtonHeaderTypography isActive={isActive} isHovered={isHovered} text={text} />
			</button>
		);
	}
	if (disabledLink) {
		return (
			<div className={classNames(classes["root"], isActive && classes["active"])} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
				<ButtonHeaderTypography isActive={isActive} isHovered={isHovered} text={text} />
			</div>
		);
	}
	return (
		<div className={classNames(classes["root"], classes["desactivated"])}>
			<ButtonHeaderTypography isActive={false} isHovered={false} text={text} disabled />
		</div>
	);
}
